import { customElement, html } from '@adobe/edex/ui/edex-lit';
import { $t } from '@adobe/edex/ui/shared/config/i18n';
import { EdexTagName } from '@adobe/edex/ui/shared/constants';
import { BaseAuthElement } from '@adobe/edex/ui/components/base.element';
import { MarshalledResource, TagItem } from '@adobe/edex/ui/types';
import { getMemberUrl } from '@adobe/edex/ui/shared/utils/routes';
import '@spectrum-web-components/link/sp-link';
import '@spectrum-web-components/avatar/sp-avatar';
import { getAvatarURL, getTagItemsFromPrimarySecondary } from '@adobe/edex/ui/utils';
import { I18nKey } from '@adobe/edex/ui/shared/constants/i18n';
import { SearchEntityType, SearchKey } from '@adobe/edex/ui/shared/types/search';
import { isEmpty } from 'lodash-es';
import { DEFAULT_AVATAR } from '@adobe/edex/ui/shared/constants';

import(
  /* webpackChunkName: "component/resource-toolbar-element-styles" */
  /* webpackPreload: true */
  /* webpackMode: "lazy" */
  '@adobe/edex/ui/components/resource/resource-meta/resource-meta.scss'
);

@customElement(EdexTagName.resourceMeta)
export class ResourceMeta extends BaseAuthElement {
  get useLightDom() {
    return true;
  }

  static get properties() {
    return {
      ...super.properties,
      resource: { attribute: false },
    };
  }

  resource: MarshalledResource;

  get subjectItems() {
    const {
      resource: { subjects },
    } = this;

    return getTagItemsFromPrimarySecondary(subjects, SearchKey.subject, SearchEntityType.resources);
  }

  get ageItems() {
    const {
      resource: { academicLevels },
    } = this;

    return getTagItemsFromPrimarySecondary(academicLevels, SearchKey.ageLevel, SearchEntityType.resources);
  }

  renderNonAdobeForEducation(author) {
    const { vanityURL, fullName, avatarURL } = author;
    return html`
      <sp-link href="${getMemberUrl(vanityURL)}" target="_blank">
        <img
          class="resource-author-avatar"
          label=${fullName}
          src="${getAvatarURL(avatarURL)}"
          onerror="this.onerror=null;this.src='${DEFAULT_AVATAR}';"
        />
      </sp-link>
      <div class="resource-author-meta">
        <sp-link href="${getMemberUrl(vanityURL)}" quiet target="_blank">${fullName}</sp-link>
      </div>
    `;
  }

  renderAdobeForEducationWithAuthor(author, secondaryAuthor) {
    const { avatarURL } = author;
    return html`
      <sp-link href="${secondaryAuthor.profileURL}" target="_blank">
        <sp-avatar label=${secondaryAuthor.name} src="${getAvatarURL(avatarURL)}"></sp-avatar>
      </sp-link>
      <div class="resource-author-meta">
        <sp-link href="${secondaryAuthor.profileURL}" quiet target="_blank">${secondaryAuthor.name}</sp-link>
        <sp-field-label class="secondary-author-meta-label">
          ${$t(I18nKey.resourceBuilderSecondaryAuthorNameDescription)}
        </sp-field-label>
      </div>
    `;
  }

  renderContributorsAndViews = () => {
    const {
      resource: { author, viewCount, secondaryAuthor },
    } = this;

    return html`
      <div class="contributors-views">
        <div class="resource-authors">
          ${secondaryAuthor && !isEmpty(secondaryAuthor.name)
            ? this.renderAdobeForEducationWithAuthor(author, secondaryAuthor)
            : this.renderNonAdobeForEducation(author)}
        </div>
        <span class="view-count">${viewCount.toLocaleString()} ${$t(I18nKey.views)}</span>
      </div>
    `;
  };

  renderMetaItems = (items: TagItem[], key: SearchKey) => {
    return html`
      <div class="meta-items ${key}">
        ${items.map(
          ({ label, url }) => html`
            <sp-link target="_blank" href="${url}">${$t(label)}</sp-link>
          `
        )}
      </div>
    `;
  };

  renderMeta = () => {
    const { ageItems, subjectItems } = this;
    return html`
      ${this.renderMetaItems(ageItems, SearchKey.ageLevel)}${this.renderMetaItems(subjectItems, SearchKey.subject)}
    `;
  };

  render() {
    const {
      resource: { subjects, academicLevels },
    } = this;
    if (!this.resource) {
      return null;
    }

    return html`
      <div class="resource-meta">${this.renderContributorsAndViews()} ${this.renderMeta()}</div>
    `;
  }
}
